// SCR-STAFF-1007 — تمدید اعتبارنامه بیمه‌گر (AP-INT-09؛ US-INT-107): مقصد لینک پیامکی، پس از ورود ققنوس همان کاربر.
//
// صفحه هیچ داده نسخه‌ای نشان نمی‌دهد: فقط بیمه‌گر، شعبه و **شمار** ارسال‌های در انتظار (BR-INT-097). لینک یک‌بارمصرف و
// ۱۵ دقیقه‌ای است و کاربر غیرمالک همان پیام `PHX-INT-0313` را می‌گیرد.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type CredentialRenewalTicket } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, ListPage, LoadingState } from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

export const SCR_STAFF_1007 = 'SCR-STAFF-1007';

export function CredentialRenewalPage() {
  const { t } = useTranslation('int');
  const { tenantApi } = useTenantSession();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [ticket, setTicket] = useState<CredentialRenewalTicket | null>(null);

  const consume = useMutation({
    mutationFn: (value: string) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/renewals/{token}:consume', {
          params: { path: { token: value } },
        }),
      ),
    onSuccess: setTicket,
  });

  // لینک **یک‌بارمصرف** است: فقط در نخستین باز شدن صفحه مصرف می‌شود.
  useEffect(() => {
    if (token !== '' && consume.isIdle) {
      consume.mutate(token);
    }
  }, [token, consume]);

  return (
    <ListPage screenId={SCR_STAFF_1007} titleKey="int:credential_renewal.title" subtitleKey="int:credential_renewal.subtitle">
      {token === '' ? (
        <Alert severity="error" data-testid="credential-renewal-invalid">
          {t('int:credential_renewal.invalid')}
        </Alert>
      ) : consume.isPending ? (
        <LoadingState />
      ) : consume.error ? (
        <Stack spacing={1}>
          <Alert severity="error" data-testid="credential-renewal-invalid">
            {t('int:credential_renewal.invalid')}
          </Alert>
          <ErrorState error={consume.error} compact />
        </Stack>
      ) : ticket !== null ? (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="credential-renewal-card">
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">
              {t(`int:insurer_credentials.insurer.${ticket.insurer}`, { defaultValue: ticket.insurer })}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="credential-renewal-pending">
              {t('int:credential_renewal.pending', { count: faDigits(ticket.pendingCount) })}
            </Typography>
            <Alert severity="info">{t('int:credential_renewal.hint')}</Alert>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(ticket.loginMethods ?? []).map((method) => (
                <Button
                  key={method}
                  variant="contained"
                  href={
                    method === 'TaminOAuthPkce'
                      ? '/settings/integrations/insurer-credentials'
                      : '/settings/integrations/insurer-credentials#provider-credential-matrix'
                  }
                  data-testid={`credential-renewal-method-${method}`}
                >
                  {t(`int:credential_renewal.methods.${method}`, { defaultValue: method })}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Paper>
      ) : null}
    </ListPage>
  );
}
